import React, { useContext, useState, Suspense } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaQuestionCircle } from "react-icons/fa";

import NavLinks from "./NavLinks";
import NotificationBell from "components/notifications/NotificationBell";
import SideDrawer from "./SideDrawer";
import Backdrop from "shared/components/UIElements/Backdrop";
import DemoAccountBadge from "./components/DemoAccountBadge";
import { DemoLeadCaptureContext } from "components/demo/DemoLeadCaptureContext";
import { trackSuiteEvent } from "shared/util/track";
import HelpDropdown from "./components/HelpDropdown";
import SettingsDropdown from "./components/SettingsDropdown";
import ImpersonationBanner from "components/ImpersonationBanner";
import { AiChatContext } from "features/ai-chat/AiChatContext";
import AiChatWidget from "features/ai-chat/AiChatWidget";
import "./MainNavigation.css";
import { PERMISSION_TYPES, COMPANY_FEATURES, isCustomerContactRole } from "@routebot/constants";
import { AuthContext } from "shared/context/auth-context";

import { getThemeName } from "config/ConfigUtils";
import { isInternationalTheme } from "config/ConfigUtils";

const MainNavigation = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const { toggle: toggleAiChat } = useContext(AiChatContext);
  const { openKeepAccount } = useContext(DemoLeadCaptureContext);
  const location = useLocation();

  const [drawerIsOpen, setDrawerIsOpen] = useState(false);

  const openDrawerHandler = () => {
    setDrawerIsOpen(true);
  };

  const closeDrawerHandler = () => {
    setDrawerIsOpen(false);
  };

  // /start is a normal page of the app; visitors see the brand plus sign-in actions, except on
  // the auth pages themselves where those actions would be redundant.
  const onAuthPage = ["/login", "/sign-up", "/reset-password", "/recover-password", "/verify-email", "/accept-invite"].includes(
    location.pathname,
  );
  const showGuestActions = !auth.isLoggedIn && isInternationalTheme() && !onAuthPage;

  return (
    <Suspense fallback="loading">
      <React.Fragment>
        {auth.isLoggedIn && (
          <React.Fragment>
            {drawerIsOpen && <Backdrop onClick={closeDrawerHandler} />}
            <SideDrawer show={drawerIsOpen} onClick={closeDrawerHandler}>
              <nav className="main-navigation-links-mobile">
                <NavLinks />
              </nav>
            </SideDrawer>
          </React.Fragment>
        )}

        <div className="main-navigation">
          <div className="main-navigation-left">
            {auth.isLoggedIn && (
              <React.Fragment>
                <button className="main-navigation-drawer-btn" onClick={openDrawerHandler}>
                  <span />
                  <span />
                  <span />
                </button>
              </React.Fragment>
            )}
            <h3 className="main-navigation-title">
              <Link to="/">{getThemeName()}</Link>
            </h3>
            <nav className="main-navigation-links-desktop">
              <NavLinks />
            </nav>
          </div>

          {auth.isLoggedIn && auth.company.isDemo && (
            <button
              type="button"
              className="demo-book-cta"
              onClick={() => {
                trackSuiteEvent("Demo — Keep Account (navbar)", "button_click");
                if (openKeepAccount) openKeepAccount();
              }}
            >
              <span className="demo-book-cta-pulse" />
              Keep this account
            </button>
          )}

          <div
            className="main-navigation-right"
            style={{ display: "flex", alignItems: "center", gap: "20px", justifyContent: "flex-end" }}
          >
            <ImpersonationBanner />
            {auth.isLoggedIn && auth.company.isDemo && <DemoAccountBadge />}

            {auth.isLoggedIn && (
              <ul className="nav-links" style={{ justifyContent: "flex-end" }}>
                <NavLink to="/settings">{t("settings")}</NavLink>
              </ul>
            )}

            {auth.isLoggedIn &&
              !isCustomerContactRole(auth.client?.role) &&
              ((!auth.permissions[PERMISSION_TYPES.ACCESS_TO_TRACK_VEHICLE_ONLY] &&
                auth.company.features[COMPANY_FEATURES.WEBSITE_NOTIFICATIONS]) ||
                isInternationalTheme()) && <HelpDropdown />}

            {auth.isLoggedIn &&
              !auth.permissions[PERMISSION_TYPES.ACCESS_TO_TRACK_VEHICLE_ONLY] &&
              !isCustomerContactRole(auth.client?.role) &&
              auth.company.features[COMPANY_FEATURES.WEBSITE_NOTIFICATIONS] && (
                <NotificationBell
                  items={[
                    {
                      id: "0",
                      title: "Analiz sayfası yenilikleri ve otomatik sürücü önerme",
                      description:
                        "Sistem üzerinde son dönemde yaptığımız kritik değişiklikleri aşağıdaki videodan ayrıntılı olarak görebilirsiniz.\n\n-Standart servis günlerinin ayarlanması\n-Otomatik sürücü öner mekanizması\n-Analiz sayfasındaki yeni sekmelerin ayrıntılı açıklaması\n",
                      videoUrl: "https://media.servistesin.com/servistesin_puantaj_egitim.mp4",
                    },
                    {
                      id: "1",
                      title: "Eksik Metrik Açıklaması",
                      description:
                        "Bir servis için eksik metrik ibaresi görmeniz, o servisin mesafe ya da süre değerlerinden birinin sistemde bulunamadığı anlamına gelir.\n\nBu durumda olan servislerin puantajı hesaplanamaz ve günlük programda görünmez.\n\nBu ibareyi gördüğünüz servisin ekranına girip herhangi bir harita işlemi yapmanız (yolcu ekleme, çıkarma, yolcu sırası değiştirme, optimize et tuşuna basma) yeterlidir. Harita işlemi yapılmadan manuel olarak girilen pickup saatleri bu hesabı yapmak için yeterli değildir. Servis ekranlarında haritanın sol üstünde bulunan mesafe ve süre bilgilerinin dolu olmasi gerekmektedir.",
                    },
                    {
                      id: "2",
                      title: "Personel İniş Adresleri",
                      description:
                        "Personeller için biniş ve iniş adresleri ayrı şekilde girilebilme özelliği eklenmiştir. Bniş ve iniş adressleri aynı olsa dahi iki adres bilgisini de doldurmanız gerekmektedir. Mevcut personellerin iniş adresleri otomatik olarak biniş adreslerine dönüştürülmüş olarak ayarlanmıştır",
                    },
                  ]}
                  version={"18"}
                />
              )}

            {auth.isLoggedIn &&
              !auth.permissions[PERMISSION_TYPES.ACCESS_TO_TRACK_VEHICLE_ONLY] &&
              !isCustomerContactRole(auth.client?.role) &&
              auth.company.features[COMPANY_FEATURES.WEBSITE_NOTIFICATIONS] && (
                <div className="ai-chat-header-anchor">
                  <button className="ai-chat-header-toggle" onClick={toggleAiChat} title={t("help")}>
                    <FaQuestionCircle className="ai-chat-header-icon" />
                  </button>
                  <AiChatWidget />
                </div>
              )}

            {auth.isLoggedIn && <SettingsDropdown />}

            {!auth.isLoggedIn && !isInternationalTheme() && <SettingsDropdown />}

            {showGuestActions && (
              <nav className="main-navigation-guest" aria-label="Account">
                <Link to="/login" className="main-navigation-guest__link">
                  Log in
                </Link>
                <Link to="/sign-up" className="main-navigation-guest__cta">
                  Create account
                </Link>
              </nav>
            )}
          </div>
          {/* </div> */}
        </div>
      </React.Fragment>
    </Suspense>
  );
};

export default MainNavigation;
